import { Box, Button, IconButton } from '@mui/material'
import { Link } from 'react-router-dom'
import { CustomColumnGridConfig } from '../types'
import { useAppTheme } from '@/theme/ThemeContext'
import PenIcon from '@assets/Analytics/Pen.svg'
import { formatLossPercent } from '@/utils/analyticsFormat'
import { formatConsumptionValue } from '@/utils/consumptionFormat'

const volumeCell = (params: any) =>
  params.value != null
    ? `${formatConsumptionValue(params.value, 3)} ק'`
    : '-'

const ZoneNameCell = ({ params }: { params: any }) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  if (params.value == null) return '-'

  // Two id namespaces, two routes: hierarchy rows (zoneType 1) carry a
  // root premise id → tree/…; group rows (zoneType 2) carry a group id
  // → group/… (relative to /:siteId/analytics).
  const kindSegment = params.row.zoneType === 2 ? 'group' : 'tree'

  return (
    <Button
      component={Link}
      to={`${kindSegment}/${params.row.id}/details`}
      onClick={e => e.stopPropagation()}
      sx={{
        textTransform: 'none',
        color: isDarkMode ? '#FFFFFF' : '#003F61',
        backgroundColor: isDarkMode ? '#1D325A' : '#E3F5FF',
        borderRadius: '20px',
        px: 2,
        py: 0.3,
        fontSize: '13px',
        fontWeight: 500,
        fontFamily: 'Rubik, sans-serif',
        textDecoration: 'none',
        minWidth: '90px',
        '&:hover': { backgroundColor: isDarkMode ? '#284373' : '#D0EEFF' },
      }}
    >
      {params.value}
    </Button>
  )
}

const EditZoneCell = ({
  params,
  onEdit,
}: {
  params: any
  onEdit?: (row: any) => void
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  return (
    <IconButton
      size="small"
      aria-label="עריכת עץ מים"
      onClick={e => {
        e.stopPropagation()
        onEdit?.(params.row)
      }}
      onDoubleClick={e => e.stopPropagation()}
    >
      <Box
        component="img"
        src={PenIcon}
        alt=""
        sx={{
          width: 16,
          filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
        }}
      />
    </IconButton>
  )
}

// Builder (CRM-config idiom): the page injects the open-editor callback.
// The actions column stays LAST — in this RTL layout the last column
// renders leftmost, at the row's end.
const buildColumnsConfig = (
  onEditZone?: (row: any) => void
): CustomColumnGridConfig[] => [
  {
    field: 'zoneName',
    flex: 1.5,
    headerName: 'שם העץ',
    renderCell: (params: any) => <ZoneNameCell params={params} />,
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'zoneType',
    flex: 0.7,
    minWidth: 90,
    headerName: 'סוג העץ',
    valueGetter: (_value: any, row: any) => {
      const val = row?.zoneType ?? _value
      return val === 1 || val === '1'
        ? 'עץ'
        : val === 2 || val === '2'
          ? 'קבוצה'
          : (val ?? '-')
    },
    renderCell: (params: any) =>
      params.value === 'עץ' || params.value === 'קבוצה'
        ? params.value
        : params.value === 1 || params.value === '1'
          ? 'עץ'
          : params.value === 2 || params.value === '2'
            ? 'קבוצה'
            : '-',
    filter: { type: 'options' },
    filterable: true,
  },
  {
    field: 'zoneTypeName',
    flex: 1,
    minWidth: 110,
    headerName: 'קטגוריה',
    renderCell: (params: any) => params.value ?? '-',
    filter: { type: 'none' },
    filterable: false,
  },
  {
    field: 'mainAddress',
    flex: 1.4,
    minWidth: 150,
    headerName: 'כתובת ראשי',
    renderCell: (params: any) => params.value || '-',
    filter: { type: 'none' },
    filterable: false,
  },
  {
    field: 'number',
    flex: 1,
    headerName: "מס' עצי מים",
    type: 'number',
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'suppliers',
    flex: 1,
    headerName: 'ספקים',
    type: 'number',
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'consumers',
    flex: 1.5,
    headerName: 'צרכנים',
    type: 'number',
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'supplied',
    flex: 1,
    headerName: 'סופק',
    type: 'number',
    renderCell: volumeCell,
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'suppliedNight',
    flex: 1,
    headerName: 'סופק לילה',
    type: 'number',
    renderCell: volumeCell,
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'consumed',
    flex: 1,
    headerName: 'נצרך',
    type: 'number',
    renderCell: volumeCell,
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'consumedNight',
    flex: 1,
    headerName: 'נצרך לילה',
    type: 'number',
    renderCell: volumeCell,
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'lossPercent',
    flex: 1,
    headerName: 'אחוז פחת',
    type: 'number',
    renderCell: (params: any) => formatLossPercent(params.value),
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'sharedConsumption',
    flex: 1.2,
    headerName: 'צריכה משותפת',
    type: 'number',
    renderCell: volumeCell,
    filter: { type: 'aggregated' },
    filterable: true,
  },
  {
    field: 'actions',
    headerName: '',
    width: 56,
    minWidth: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    disableReorder: true,
    filter: { type: 'none' },
    renderCell: (params: any) => (
      <EditZoneCell params={params} onEdit={onEditZone} />
    ),
  },
]

export default buildColumnsConfig
